import { Card, Icon, Text, Tooltip } from '@nike/eds'
import './ShipmentMilestones.scss'
import { HalfCheckedIcon } from './HalfCheckedIcon'
import { Shipment } from '../../../../generated/graphql'
import { idpDate, idpDateTimeWindow } from '../../../../common/utils/formatter'
import { get } from '../../../../common/components/properties/Properties'
import { milestoneConfig } from './ShipmentMilestoneColumns'
import { EmptyCircleIcon } from './EmptyCircleIcon'
import { CheckCircleFilled, Close } from '@nike/nike-design-system-icons'
import { useMemo } from 'react'

export enum MilestoneIcon {
  Planned = 'No information',
  Partial = 'Partially completed',
  Completed = 'Milestone completed',
  Cancelled = 'Milestone cancelled',
}

export enum MilestoneSize {
  Large = 'l',
  Medium = 'm',
}

function getIcon(iconName: MilestoneIcon, size: MilestoneSize, isGreyedOut?: boolean) {
  if (iconName === MilestoneIcon.Partial) {
    return isGreyedOut ? (
      <HalfCheckedIcon size={size} className="milestone-icon" color="grey" />
    ) : (
      <HalfCheckedIcon size={size} className="milestone-icon" />
    )
  } else {
    if (iconName === MilestoneIcon.Planned) {
      return isGreyedOut ? (
        <EmptyCircleIcon size={size} className="milestone-icon" color="grey" />
      ) : (
        <EmptyCircleIcon size={size} className="milestone-icon" />
      )
    } else {
      if (iconName === MilestoneIcon.Completed) {
        return isGreyedOut ? (
          <CheckCircleFilled size={size} className="milestone-icon" color="grey" />
        ) : (
          <CheckCircleFilled size={size} className="milestone-icon" />
        )
      } else {
        return <Close size={size} className="milestone-icon" />
      }
    }
  }
}

export interface MilestoneColumn {
  readonly header: string
  readonly getIconName: (s: Shipment) => MilestoneIcon
  readonly planned?: string
  readonly getActualDate?: (s: Shipment) => any
  readonly getPlannedDate?: (s: Shipment) => any
  readonly getRebookingDate?: (s: Shipment) => any
  readonly isColumnVisible?: (s: Shipment) => boolean
  readonly isGreyedOut?: (s: Shipment) => boolean
}

export function ShipmentMilestones({ shipment }: { shipment: Shipment }) {
  const visibleMilestoneColumns = useMemo(
    () =>
      milestoneConfig.filter(
        ({ isColumnVisible }) => !isColumnVisible || isColumnVisible(shipment)
      ),
    [shipment]
  )

  return (
    <div>
      <Tooltip
        className="shipment-milestones-tooltip"
        placement="right"
        bodySlot={
          <table className="shipment-milestones-tooltip-table">
            <tbody>
              {Object.keys(MilestoneIcon).map((key) => (
                <tr key={`icon-${key}`}>
                  <th>{getIcon(MilestoneIcon[key], MilestoneSize.Medium)}</th>
                  <th>{MilestoneIcon[key]}</th>
                </tr>
              ))}
            </tbody>
          </table>
        }
      >
        <Text as="h2" font="title-3" style={{ margin: '0 0 20px 12px' }} className="vcenter">
          {'Milestones'}
          <Icon name="InfoCircle" size="m" className="eds-spacing--ml-8" />
        </Text>
      </Tooltip>

      <Card padding={12} className="shipment-table-styling">
        <table className="shipment-milestones-table">
          <thead className="shipment-table-head">
            <tr className={shipment.shipmentCancelTimestamp ? 'icon-row-grey' : 'icon-row'}>
              <th></th>
              {visibleMilestoneColumns.map(({ header, getIconName, isGreyedOut }) =>
                isGreyedOut(shipment) ? (
                  <th key={`header-${header}`}>
                    <div style={{ color: 'grey' }}>{header}</div>
                    {getIcon(getIconName(shipment), MilestoneSize.Large, true)}
                  </th>
                ) : (
                  <th key={`header-${header}`}>
                    <div>{header}</div>
                    {getIcon(getIconName(shipment), MilestoneSize.Large)}
                  </th>
                )
              )}
            </tr>
          </thead>
          <tbody className="shipment-table-body">
            <tr className="planned-row">
              <th>Planned</th>
              {visibleMilestoneColumns.map(({ header, getPlannedDate, isGreyedOut }) =>
                isGreyedOut(shipment) ? (
                  <td style={{ color: 'grey' }} key={header}>
                    {getPlannedDate && getPlannedDate(shipment)}
                  </td>
                ) : (
                  <td key={header}>{getPlannedDate && getPlannedDate(shipment)}</td>
                )
              )}
            </tr>
            {containsRebookingTimestamp(shipment) && (
              <tr className="rebooking-row">
                <th>Rebooking</th>
                {visibleMilestoneColumns.map(({ header, getRebookingDate }) => (
                  <td key={header}>{getRebookingDate && getRebookingDate(shipment)}</td>
                ))}
              </tr>
            )}
            <tr className="actual-row">
              <th>Actual</th>
              {visibleMilestoneColumns.map(({ header, getActualDate, isGreyedOut }) =>
                isGreyedOut(shipment) ? (
                  <td style={{ color: 'grey' }} key={header}>
                    {getActualDate && getActualDate(shipment)}
                  </td>
                ) : (
                  <td key={header}>{getActualDate && getActualDate(shipment)}</td>
                )
              )}
            </tr>
          </tbody>
        </table>
      </Card>
    </div>
  )
}

function eddMilestoneDate(
  shipment: Shipment,
  isExport: boolean,
  dateFormat: (value: string) => string
) {
  const dateFormatted = dateFormat(get(shipment, 'calculatedEstimatedDeliveryTimestamp'))
  const transitDays = get(shipment, 'eddCalculation.transitDays')
  return isExport ? (
    dateFormatted
  ) : (
    <p>
      {dateFormatted}
      {typeof transitDays === 'number' && (
        <Tooltip
          bodySlot={
            <>
              <p>Transit Days: {transitDays}</p>
            </>
          }
          isDark={true}
        >
          <Icon name="InfoCircle" size="s" className="milestone-edd-info-icon" />
        </Tooltip>
      )}
    </p>
  )
}

export function getPlannedDeliveryDate({
  shipment,
  dateFormat,
  isExport = false,
}: {
  shipment: Shipment
  dateFormat: (value: string) => string
  isExport?: boolean
}) {
  if (shipment.iDPIndicator) {
    if (containsDeliveryTimeWindow(shipment)) {
      return idpDateTimeWindow(
        get(shipment, 'plannedDeliveryFromTimestamp'),
        get(shipment, 'plannedDeliveryToTimestamp')
      )
    } else {
      return idpDate(get(shipment, 'iDPPlannedTimestamp'))
    }
  } else if (get(shipment, 'calculatedEstimatedDeliveryTimestamp')) {
    return eddMilestoneDate(shipment, isExport, dateFormat)
  }
  return dateFormat(get(shipment, 'estimatedDeliveryDate'))
}

export function getRebookingDate({ shipment }: { shipment: Shipment }) {
  const dateFrom = get(shipment, 'rebooking.plannedDeliveryFromTimestamp')
  const dateTo = get(shipment, 'rebooking.plannedDeliveryToTimestamp')
  return shipment.iDPIndicator && containsDeliveryTimeWindow(shipment) && dateFrom && dateTo
    ? idpDateTimeWindow(dateFrom, dateTo)
    : idpDate(get(shipment, 'rebooking.iDPPlannedTimestamp'))
}

function containsRebookingTimestamp(shipment) {
  return (
    get(shipment, 'rebooking.iDPPlannedTimestamp') ||
    (get(shipment, 'rebooking.plannedDeliveryFromTimestamp') &&
      get(shipment, 'rebooking.plannedDeliveryToTimestamp'))
  )
}

function containsDeliveryTimeWindow(shipment) {
  return (
    get(shipment, 'plannedDeliveryToTimestamp') && get(shipment, 'plannedDeliveryFromTimestamp')
  )
}
